// --- React libraries ---
import React, { useEffect, useState } from "react";

// --- types ---
import { SharedContextProps } from "../../types/shared";

interface RemoteAppletLoaderProps {
  definitionId: number;
  sheetData?: any;
  user?: any;
  domainId?: string | null;
  // Add props for shared contexts/data as needed (e.g., user, theme)
}

const RemoteAppletLoader: React.FC<RemoteAppletLoaderProps> = ({
  definitionId,
  sheetData,
  user,
  domainId,
  ...props
}) => {
  const [Component, setComponent] = useState<React.ComponentType<any> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [bundleUrl, setBundleUrl] = useState<string | null>(null);

  // Add this utility function near the top of the file
  const retryFetch = async (
    url: string,
    options: RequestInit = {},
    retries = 5,
    delay = 1000,
  ): Promise<Response> => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response;
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return retryFetch(url, options, retries - 1, delay * 2); // Exponential backoff
      }
      throw error;
    }
  };

  // First, fetch the AppletDefinition to get the bundleUrl
  useEffect(() => {
    const fetchDefinition = async () => {
      if (!definitionId) throw new Error("Definition ID is undefined");
      try {
        const response = await retryFetch(
          `${import.meta.env.VITE_API_URL}/api/applet-definitions/${definitionId}`,
        );
        const data = await response.json();
        console.log("Fetched definition data:", data);
        setBundleUrl(data.bundleUrl);
      } catch (err) {
        console.error("Fetch definition error:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch definition",
        );
      }
    };

    fetchDefinition();
  }, [definitionId]);

  // Then, load the bundle once bundleUrl is available
  useEffect(() => {
    if (!bundleUrl) return;

    console.log("Loading bundle from:", bundleUrl);

    const loadBundle = async () => {
      try {
        const response = await fetch(bundleUrl);
        if (!response.ok) throw new Error("Failed to fetch bundle");
        const code = await response.text();
        console.log("Bundle code length:", code.length);

        if (!window.process) {
          window.process = { env: { NODE_ENV: "production" } } as any;
        }

        // Safely execute the UMD code (assumes React/ReactDOM are global)
        const func = new Function("React", "ReactDOM", code);
        func(window.React, window.ReactDOM);

        // Assuming the UMD exports the component as window.TimekeepingApplet
        const LoadedComponent = (window as any).TimekeepingApplet
          ?.TimekeepingApplet;
        console.log("Loaded component:", LoadedComponent);
        if (LoadedComponent) {
          setComponent(() => LoadedComponent);
        } else {
          throw new Error("Component not found in bundle");
        }
      } catch (err) {
        console.error("Load bundle error:", err);
        setError(err instanceof Error ? err.message : "Unknown error");
      }
    };

    loadBundle();
  }, [bundleUrl]);

  if (error) return <div>Error loading applet: {error}</div>;
  if (!Component) return <div>Loading...</div>;

  return (
    <Component
      sheetData={sheetData}
      user={user}
      domainId={domainId}
      {...props}
    />
  );
};

export default RemoteAppletLoader;
